import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Loyalty = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const [cards, setCards] = useState([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ customer_name: '', customer_phone: '', max_punches: 10 });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${API}/loyalty-cards`);
      setCards(res.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSearch = async () => {
    try {
      const res = await axios.get(`${API}/loyalty-cards/search?phone=${searchPhone}`);
      setCards([res.data]);
    } catch (error) {
      toast.error(t('messages.cardNotFound'));
      fetchCards();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/loyalty-cards`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      setDialogOpen(false);
      setFormData({ customer_name: '', customer_phone: '', max_punches: 10 });
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error'));
    }
  };

  const handlePunch = async (cardId) => {
    try {
      const res = await axios.put(`${API}/loyalty-cards/${cardId}/punch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      fetchCards();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  return (
    <div className="loyalty">
      <div className="section-header">
        <h2 className="section-title">{t('loyalty.loyaltyCards')}</h2>
        <div className="header-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('loyalty.searchByPhone')}
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              data-testid="search-phone-input"
            />
            <button onClick={handleSearch} className="search-btn" data-testid="search-btn">
              <Search size={16} />
            </button>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-loyalty-card-btn">
            <Plus size={16} /> {t('loyalty.addLoyaltyCard')}
          </Button>
        </div>
      </div>

      <div className="cards-grid">
        {cards.map((card) => (
          <div key={card.id} className="loyalty-card" data-testid={`loyalty-card-${card.id}`}>
            <div className="card-header">
              <Heart size={24} fill="#ef4444" color="#ef4444" />
              <div className="rewards-badge" data-testid="rewards-earned">{card.rewards_earned} rewards</div>
            </div>
            <div className="card-body">
              <div className="customer-name" data-testid="customer-name">{card.customer_name}</div>
              <div className="customer-phone">{card.customer_phone}</div>
              <div className="punches" data-testid="punches">
                {Array.from({ length: card.max_punches }).map((_, idx) => (
                  <div key={idx} className={`punch ${idx < card.punches ? 'filled' : ''}`} />
                ))}
              </div>
              <div className="punch-count" data-testid="punch-count">
                {card.punches}/{card.max_punches}
              </div>
            </div>
            <Button onClick={() => handlePunch(card.id)} className="w-full" data-testid="punch-card-btn">
              {t('loyalty.punchCard')}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="loyalty-card-dialog" aria-describedby="loyalty-dialog-description">
          <DialogHeader>
            <DialogTitle>{t('loyalty.addLoyaltyCard')}</DialogTitle>
            <p id="loyalty-dialog-description" className="text-sm text-gray-500">
              Create a new loyalty card for a customer with their details.
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>{t('loyalty.customerName')}</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
                data-testid="customer-name-input"
              />
            </div>
            <div className="form-group">
              <label>{t('loyalty.customerPhone')}</label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                data-testid="customer-phone-input"
              />
            </div>
            <div className="form-group">
              <label>Max Punches</label>
              <input
                type="number"
                value={formData.max_punches}
                onChange={(e) => setFormData({ ...formData, max_punches: parseInt(e.target.value) })}
                required
                data-testid="max-punches-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-loyalty-card-btn">{t('actions.save')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <style>{`
        .loyalty {
          width: 100%;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .search-box {
          display: flex;
          gap: 8px;
        }

        .search-box input {
          width: 200px;
        }

        .search-btn {
          padding: 10px 14px;
          background: #3b82f6;
          color: white;
          border-radius: 8px;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .loyalty-card {
          background: hsl(var(--card));
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid hsl(var(--border));
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .rewards-badge {
          background: #fef3c7;
          color: #92400e;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .card-body {
          margin-bottom: 16px;
        }

        .customer-name {
          font-size: 18px;
          font-weight: 700;
          color: hsl(var(--foreground));
          margin-bottom: 4px;
        }

        .customer-phone {
          font-size: 13px;
          color: hsl(var(--muted-foreground));
          margin-bottom: 16px;
        }

        .punches {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }

        .punch {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid hsl(var(--border));
          background: hsl(var(--background));
        }

        .punch.filled {
          background: #ef4444;
          border-color: #ef4444;
        }

        .punch-count {
          font-size: 14px;
          color: hsl(var(--muted-foreground));
          font-weight: 600;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default Loyalty;
